/**
 * Bulk Processing Page
 *
 * Dedicated workflow for high-volume processing:
 * 1. Bulk Upload image(s)
 * 2. Select Bulk Service (Staging or Destaging)
 * 3. Configure Service (if applicable)
 * 4. Create order(s) with credit validation
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - NO localStorage
 * - Auto-scroll with 300ms delay
 * - Credit validation BEFORE order creation
 * - User-friendly error handling
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebaseConfig';
import { ServiceType } from '@/shared/types';
import { Button, Card } from '@/components/ui';
import { ServiceConfig, ServiceConfigData } from '@/components/ServiceConfig';
import { BulkProgress } from '@/components/BulkProgress';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { useToast } from '@/contexts/ToastContext';
import { useImageUpload } from '@/hooks/useImageUpload';
import { useAutoScroll } from '@/hooks/useAutoScroll';
import {
  validateImageFile,
  validateCredits,
} from '@/utils/validation';
import {
  getServiceName,
  getServiceCost,
  formatFileSize,
} from '@/utils/formatting';

interface UploadedFile {
  file: File;
  previewUrl: string;
  downloadUrl?: string;
  status: 'pending' | 'uploading' | 'uploaded' | 'error';
  error?: string;
}

export function Upload(): React.JSX.Element {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { credits, storageUsed, storageLimit } = useCredits();
  const { showToast } = useToast();
  const { uploadImage } = useImageUpload();
  const { scrollTo } = useAutoScroll();

  // Form state
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
  const [serviceConfig, setServiceConfig] = useState<ServiceConfigData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Processing state
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processedCount, setProcessedCount] = useState<number>(0);

  /**
   * Handle file selection
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const newUploadedFiles: UploadedFile[] = [];
    let currentStorageUsed = storageUsed;

    for (const file of files) {
      // Validate file
      const fileError = validateImageFile(file);
      if (fileError) {
        showToast(`${file.name}: ${fileError}`, 'error');
        continue;
      }

      // Check storage limit
      if (currentStorageUsed + file.size > storageLimit) {
        showToast(`Storage limit exceeded. Cannot add ${file.name}`, 'error');
        break;
      }

      currentStorageUsed += file.size;
      newUploadedFiles.push({
        file,
        previewUrl: URL.createObjectURL(file),
        status: 'pending',
      });
    }

    if (newUploadedFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);
      setErrors({ ...errors, file: '' });

      // Auto-scroll to service selection if this is the first batch
      if (uploadedFiles.length === 0) {
        scrollTo('service-selection', 300);
      }
    }
  };

  /**
   * Remove a file from the list
   */
  const removeFile = (index: number): void => {
    setUploadedFiles((prev) => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].previewUrl);
      newFiles.splice(index, 1);
      return newFiles;
    });

    if (uploadedFiles.length === 1) {
      setSelectedService(null);
      setServiceConfig({});
    }
  };

  /**
   * Handle service selection
   */
  const handleServiceSelect = (service: ServiceType): void => {
    setSelectedService(service);
    setServiceConfig({});
    setErrors({});

    // Auto-scroll to configuration if applicable
    if (service === ServiceType.STAGING) {
      scrollTo('service-config', 300);
    } else {
      scrollTo('review-order', 300);
    }
  };

  /**
   * Validate configuration before submission
   */
  const validateConfiguration = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (selectedService === ServiceType.STAGING) {
      if (!serviceConfig.stagingStyle) {
        newErrors.stagingStyle = 'Please select a staging style';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle order submission
   */
  const handleSubmit = async (): Promise<void> => {
    if (!currentUser || !selectedService || uploadedFiles.length === 0) return;

    // Validate configuration
    if (!validateConfiguration()) {
      showToast('Please complete all required fields', 'error');
      return;
    }

    // Validate credits
    const costPerImage = getServiceCost(selectedService);
    const totalCost = costPerImage * uploadedFiles.length;

    const creditError = validateCredits(credits, totalCost);
    if (creditError) {
      showToast(creditError, 'error');
      setErrors({ ...errors, credits: creditError });
      return;
    }

    setIsUploading(true);
    setIsProcessing(true);
    setProcessedCount(0);

    try {
      const createOrderFunc = httpsCallable(functions, 'createOrder');
      const updatedFiles = [...uploadedFiles];

      // 1. Upload all pending files
      for (let i = 0; i < updatedFiles.length; i++) {
        if (updatedFiles[i].status === 'pending') {
          try {
            updatedFiles[i].status = 'uploading';
            setUploadedFiles([...updatedFiles]);

            const result = await uploadImage(updatedFiles[i].file);
            updatedFiles[i].downloadUrl = result.downloadUrl;
            updatedFiles[i].status = 'uploaded';
            setUploadedFiles([...updatedFiles]);
          } catch (error) {
            console.error(`Error uploading ${updatedFiles[i].file.name}:`, error);
            updatedFiles[i].status = 'error';
            updatedFiles[i].error = 'Upload failed';
            setUploadedFiles([...updatedFiles]);
          }
        }
      }

      // 2. Create orders for successfully uploaded files
      for (const file of updatedFiles) {
        if (file.status === 'uploaded' && file.downloadUrl) {
          try {
            const requestData = {
              serviceType: selectedService,
              inputImageUrl: file.downloadUrl,
              // Pass additional config
              ...(selectedService === ServiceType.STAGING && {
                stagingStyle: serviceConfig.stagingStyle,
              }),
              ...(selectedService === ServiceType.ITEM_REMOVAL && {
                removalPrompt: serviceConfig.removalPrompt,
                maskCoordinates: serviceConfig.maskCoordinates,
              }),
            };

            await createOrderFunc(requestData);
            setProcessedCount((prev) => prev + 1);
          } catch (error) {
            console.error(`Error creating order for ${file.file.name}:`, error);
            // Continue with other files
          }
        }
      }

      showToast('Orders processed successfully!', 'success');

      // Navigate to gallery
      setTimeout(() => {
        navigate('/gallery');
      }, 1500);

    } catch (error) {
      console.error('Bulk processing error:', error);
      showToast('Some orders failed to process. Please check the gallery.', 'info');
    } finally {
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  const serviceCost = selectedService ? getServiceCost(selectedService) : 0;
  const totalCost = serviceCost * uploadedFiles.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bulk Processing</h1>
              <p className="text-sm text-gray-500">Upload multiple images to Stage or Destage entire properties at once.</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Credits: <span className="font-semibold text-green-600">{credits}</span>
              </span>
              <Button variant="secondary" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Progress Indicator */}
        {(isUploading || isProcessing) && (
          <BulkProgress
            totalFiles={uploadedFiles.length}
            uploadedCount={uploadedFiles.filter(f => f.status === 'uploaded').length}
            processedCount={processedCount}
            isProcessing={isProcessing}
          />
        )}

        {/* Step 1: Upload Image */}
        <Card>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Step 1: Upload Image(s)</h2>
              <span className="text-sm text-gray-500">
                {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} selected
              </span>
            </div>

            <div className="mb-6">
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-200"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg
                    className="w-8 h-8 mb-2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop multiple photos here
                  </p>
                  <p className="text-xs text-gray-500">JPG, PNG, WEBP (Up to 50 files per batch)</p>
                </div>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  disabled={isUploading || isProcessing}
                />
              </label>
            </div>

            {/* File List */}
            {uploadedFiles.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <img
                      src={file.previewUrl}
                      alt="Preview"
                      className="w-16 h-16 object-cover rounded-md"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.file.size)}
                      </p>
                      {file.status === 'error' && (
                        <p className="text-xs text-red-600">{file.error}</p>
                      )}
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      disabled={isUploading || isProcessing}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 text-sm text-gray-600">
              <p>
                Storage: {formatFileSize(storageUsed)} / {formatFileSize(storageLimit)}
              </p>
            </div>
          </div>
        </Card>

        {/* Step 2: Select Bulk Service */}
        {uploadedFiles.length > 0 && (
          <div id="service-selection">
            <Card>
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Step 2: Select Bulk Service</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Virtual Staging */}
                  <button
                    onClick={() => handleServiceSelect(ServiceType.STAGING)}
                    className={`relative overflow-hidden p-6 rounded-xl border-2 transition-all duration-200 text-left ${selectedService === ServiceType.STAGING
                      ? 'border-green-500 ring-2 ring-green-200 bg-green-50'
                      : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-4xl">🛋️</span>
                      <span className="px-3 py-1 text-xs font-medium bg-white rounded-full border border-gray-200">
                        {getServiceCost(ServiceType.STAGING)} Credits/img
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Virtual Staging</h3>
                    <p className="text-sm text-gray-600">Add furniture to empty rooms.</p>
                  </button>

                  {/* Virtual Destaging */}
                  <button
                    onClick={() => handleServiceSelect(ServiceType.DESTAGING)}
                    className={`relative overflow-hidden p-6 rounded-xl border-2 transition-all duration-200 text-left ${selectedService === ServiceType.DESTAGING
                      ? 'border-red-500 ring-2 ring-red-200 bg-red-50'
                      : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-4xl">🏚️</span>
                      <span className="px-3 py-1 text-xs font-medium bg-white rounded-full border border-gray-200">
                        {getServiceCost(ServiceType.DESTAGING)} Credits/img
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Virtual Destaging</h3>
                    <p className="text-sm text-gray-600">Remove furniture from cluttered rooms.</p>
                  </button>
                </div>

                {/* Single Image Services */}
                {uploadedFiles.length === 1 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Single Image Services</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Item Removal */}
                      <button
                        onClick={() => handleServiceSelect(ServiceType.ITEM_REMOVAL)}
                        className={`relative overflow-hidden p-6 rounded-xl border-2 transition-all duration-200 text-left ${selectedService === ServiceType.ITEM_REMOVAL
                            ? 'border-purple-500 ring-2 ring-purple-200 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-4xl">🧹</span>
                          <span className="px-3 py-1 text-xs font-medium bg-white rounded-full border border-gray-200">
                            {getServiceCost(ServiceType.ITEM_REMOVAL)} Credits/img
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Item Removal</h3>
                        <p className="text-sm text-gray-600">Remove objects or clutter from a photo.</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Step 3: Configure Service (Staging & Item Removal Only) */}
        {(selectedService === ServiceType.STAGING || selectedService === ServiceType.ITEM_REMOVAL) && (
          <div id="service-config">
            <Card>
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Step 3: Configure {getServiceName(selectedService)}
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  This style will be applied to all {uploadedFiles.length} images.
                </p>
                <ServiceConfig
                  serviceType={selectedService}
                  config={serviceConfig}
                  onChange={setServiceConfig}
                  errors={errors}
                  imageUrl={selectedService === ServiceType.ITEM_REMOVAL ? uploadedFiles[0]?.previewUrl : undefined}
                />
              </div>
            </Card>
          </div>
        )}

        {/* Step 4: Review and Submit */}
        {selectedService && uploadedFiles.length > 0 && (
          <div id="review-order">
            <Card className="bg-gradient-to-r from-blue-50 to-green-50">
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Bulk Operation: {getServiceName(selectedService)}
                </h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Quantity:</span>
                    <span className="font-semibold text-gray-900">{uploadedFiles.length} images</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Calculation:</span>
                    <span className="font-medium text-gray-900">
                      {serviceCost} credits x {uploadedFiles.length} images
                    </span>
                  </div>
                  <div className="flex justify-between text-lg border-t border-gray-200 pt-2 mt-2">
                    <span className="font-bold text-gray-900">Total Cost:</span>
                    <span className="font-bold text-blue-600">{totalCost} credits</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Your Balance:</span>
                    <span className="font-semibold text-green-600">{credits} credits</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-3">
                    <span className="text-gray-700">Remaining After:</span>
                    <span className={`font-semibold ${credits - totalCost < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {credits - totalCost} credits
                    </span>
                  </div>
                </div>

                {errors.credits && (
                  <div className="bg-red-50 border border-red-500 text-red-700 px-4 py-3 rounded-lg mb-4">
                    <p>{errors.credits}</p>
                    <Button variant="primary" className="mt-2" onClick={() => navigate('/billing')}>
                      Buy Credits
                    </Button>
                  </div>
                )}

                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isUploading || isProcessing}
                  loading={isUploading || isProcessing}
                  className="w-full text-lg py-3"
                >
                  {isUploading || isProcessing ? 'Processing...' : `Process ${uploadedFiles.length} Images`}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
